import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorDetail, setErrorDetail] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    // The link opens a fresh page: who is signed in is known only once /auth/me answers.
    // Deciding before that sent people who were signed in to the sign-in page.
    if (calledRef.current || authLoading) return;
    calledRef.current = true;

    if (!token) {
      setStatus('error');
      setErrorDetail(t('verify_email.page_error_invalid'));
      return;
    }

    authApi.verifyEmail(token)
      .then(async () => {
        setStatus('success');
        if (isAuthenticated && refreshUser) {
          await refreshUser().catch(() => {});
        }
        const target = isAuthenticated ? '/app' : '/login';
        setTimeout(() => navigate(target, { replace: true }), 1500);
      })
      .catch((err) => {
        const detail = err.response?.data?.detail || '';
        setStatus('error');
        if (/expired/i.test(detail)) {
          setErrorDetail(t('verify_email.page_error_expired'));
        } else {
          setErrorDetail(t('verify_email.page_error_invalid'));
        }
      });
  }, [token, authLoading, isAuthenticated, refreshUser, navigate, t]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-4">
      <SEO title="Verify Email" noindex={true} />
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <BusyBeeLogo className="h-10" />
        </Link>

        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
            <h1 className="text-2xl font-semibold text-white">
              {t('verify_email.page_verifying')}
            </h1>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-semibold text-white">
              {t('verify_email.page_success')}
            </h1>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-semibold text-white">
              {t('verify_email.page_error_title')}
            </h1>
            <p className="text-gray-400">{errorDetail}</p>
            <Link
              to={isAuthenticated ? '/app' : '/login'}
              className="mt-4 text-amber-400 hover:text-amber-300"
            >
              {isAuthenticated ? t('verify_email.back_to_app') : t('auth.back_to_sign_in')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
